import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
  Alert,
} from '@mui/material'
import { Add, Edit, Delete, Warning, CheckCircle, Visibility } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, Modal } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const RESULT_OPTIONS = [
  { value: 'pass', label: 'Pass', color: 'success' },
  { value: 'fail', label: 'Fail', color: 'error' },
  { value: 'adjusted', label: 'Adjusted & Pass', color: 'warning' },
]

const CalibrationsList = () => {
  const [calibrations, setCalibrations] = useState([])
  const [assets, setAssets] = useState([])
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedCalibration, setSelectedCalibration] = useState(null)
  const [dueSoon, setDueSoon] = useState([])
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    asset: '',
    calibration_date: '',
    next_calibration_date: '',
    external_calibrator: '',
    certificate_number: '',
    results: 'pass',
    notes: '',
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const [calibrationsRes, assetsRes, vendorsRes, dueSoonRes] = await Promise.all([
        api.get('/maintenance/calibrations/'),
        api.get('/assets/assets/'),
        api.get('/vendors/'),
        api.get('/maintenance/calibrations/due_soon/'),
      ])
      setCalibrations(calibrationsRes.data.results || calibrationsRes.data)
      setAssets(assetsRes.data.results || assetsRes.data)
      setVendors(vendorsRes.data.results || vendorsRes.data)
      setDueSoon(dueSoonRes.data || [])
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load calibration data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedCalibration(null)
    const today = new Date().toISOString().split('T')[0]
    const nextYear = new Date()
    nextYear.setFullYear(nextYear.getFullYear() + 1)
    setFormData({
      asset: '',
      calibration_date: today,
      next_calibration_date: nextYear.toISOString().split('T')[0],
      external_calibrator: '',
      certificate_number: '',
      results: 'pass',
      notes: '',
    })
    setFormOpen(true)
  }

  const handleEdit = (calibration) => {
    setSelectedCalibration(calibration)
    setFormData({
      asset: calibration.asset || '',
      calibration_date: calibration.calibration_date || '',
      next_calibration_date: calibration.next_calibration_date || '',
      external_calibrator: calibration.external_calibrator || '',
      certificate_number: calibration.certificate_number || '',
      results: calibration.results || 'pass',
      notes: calibration.notes || '',
    })
    setFormOpen(true)
  }

  const handleView = (calibration) => {
    setSelectedCalibration(calibration)
    setDetailOpen(true)
  }

  const handleSave = async () => {
    if (!formData.asset || !formData.calibration_date || !formData.next_calibration_date) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      const payload = {
        ...formData,
        external_calibrator: formData.external_calibrator || null,
      }
      if (selectedCalibration) {
        await api.patch(`/maintenance/calibrations/${selectedCalibration.id}/`, payload)
        toast.success('Calibration record updated')
      } else {
        await api.post('/maintenance/calibrations/', payload)
        toast.success('Calibration record created')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving calibration:', error)
      toast.error(error.response?.data?.error || 'Failed to save calibration record')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (calibration) => {
    if (!window.confirm(`Delete calibration record for ${calibration.asset_name}?`)) return
    try {
      await api.delete(`/maintenance/calibrations/${calibration.id}/`)
      toast.success('Calibration record deleted')
      fetchData()
    } catch (error) {
      console.error('Error deleting calibration:', error)
      toast.error('Failed to delete calibration record')
    }
  }

  const getResultColor = (result) => {
    const option = RESULT_OPTIONS.find((o) => o.value === result)
    return option?.color || 'default'
  }

  const columns = [
    { field: 'asset_name', headerName: 'Asset', sortable: true },
    { field: 'asset_tag', headerName: 'Tag' },
    {
      field: 'calibration_date',
      headerName: 'Calibration Date',
      renderCell: (v) => v ? new Date(v).toLocaleDateString() : '-',
      sortable: true,
    },
    {
      field: 'next_calibration_date',
      headerName: 'Next Due',
      renderCell: (v) => {
        if (!v) return '-'
        const date = new Date(v)
        const today = new Date()
        const daysUntil = Math.ceil((date - today) / (1000 * 60 * 60 * 24))
        const isOverdue = daysUntil < 0
        const isDueSoon = daysUntil >= 0 && daysUntil <= 30

        return (
          <Box display="flex" alignItems="center" gap={1}>
            {date.toLocaleDateString()}
            {isOverdue && <Chip label="Overdue" size="small" color="error" />}
            {isDueSoon && !isOverdue && <Chip label={`${daysUntil}d`} size="small" color="warning" />}
          </Box>
        )
      },
      sortable: true,
    },
    {
      field: 'results',
      headerName: 'Result',
      renderCell: (v) => (
        <Chip
          label={v?.replace('_', ' ')}
          size="small"
          color={getResultColor(v)}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    { field: 'performed_by_name', headerName: 'Performed By' },
    { field: 'certificate_number', headerName: 'Certificate #' },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleView(row) }}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row) }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Calibration Records"
        subtitle="Manage equipment calibration schedules and certificates"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Maintenance', path: '/work-orders' },
          { label: 'Calibrations' },
        ]}
        actions={
          <Button startIcon={<Add />} onClick={handleAdd}>
            Add Calibration
          </Button>
        }
      />

      {dueSoon.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
          {dueSoon.length} calibration(s) due within the next 30 days
        </Alert>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={calibrations}
          loading={loading}
          searchPlaceholder="Search calibrations..."
          emptyMessage="No calibration records found"
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedCalibration ? 'Edit Calibration Record' : 'Add Calibration Record'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {selectedCalibration ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Asset</InputLabel>
              <Select
                value={formData.asset}
                onChange={(e) => setFormData({ ...formData, asset: e.target.value })}
                label="Asset"
              >
                {assets.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Calibration Date"
              type="date"
              value={formData.calibration_date}
              onChange={(e) => setFormData({ ...formData, calibration_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Next Calibration Date"
              type="date"
              value={formData.next_calibration_date}
              onChange={(e) => setFormData({ ...formData, next_calibration_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>External Calibrator</InputLabel>
              <Select
                value={formData.external_calibrator}
                onChange={(e) => setFormData({ ...formData, external_calibrator: e.target.value })}
                label="External Calibrator"
              >
                <MenuItem value="">Internal</MenuItem>
                {vendors.filter((v) => v.is_active).map((v) => (
                  <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Result</InputLabel>
              <Select
                value={formData.results}
                onChange={(e) => setFormData({ ...formData, results: e.target.value })}
                label="Result"
              >
                {RESULT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Certificate Number"
              value={formData.certificate_number}
              onChange={(e) => setFormData({ ...formData, certificate_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Calibration Details"
        maxWidth="sm"
        actions={
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>Close</Button>
        }
      >
        {selectedCalibration && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Asset</Box>
                <Box fontWeight={600}>{selectedCalibration.asset_name}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Asset Tag</Box>
                <Box fontWeight={600}>{selectedCalibration.asset_tag}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Calibration Date</Box>
                <Box fontWeight={600}>
                  {selectedCalibration.calibration_date
                    ? new Date(selectedCalibration.calibration_date).toLocaleDateString()
                    : '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Next Due</Box>
                <Box fontWeight={600}>
                  {selectedCalibration.next_calibration_date
                    ? new Date(selectedCalibration.next_calibration_date).toLocaleDateString()
                    : '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Result</Box>
                <Chip
                  label={selectedCalibration.results?.replace('_', ' ')}
                  size="small"
                  color={getResultColor(selectedCalibration.results)}
                  sx={{ textTransform: 'capitalize', mt: 0.5 }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Certificate #</Box>
                <Box fontWeight={600}>{selectedCalibration.certificate_number || '-'}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Performed By</Box>
                <Box fontWeight={600}>{selectedCalibration.performed_by_name || '-'}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">External Calibrator</Box>
                <Box fontWeight={600}>{selectedCalibration.external_calibrator_name || 'Internal'}</Box>
              </Box>
            </Grid>
            {selectedCalibration.notes && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Notes</Box>
                  <Box>{selectedCalibration.notes}</Box>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Modal>
    </Box>
  )
}

export default CalibrationsList
